import React, { useCallback, useEffect, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import { FormInput } from 'components/form';
import { Delete } from '@mui/icons-material';
import { Box, FormControlLabel, IconButton, Switch, Tooltip } from '@mui/material';
import { CloseReason } from '../../../types';
import { useBPFCloseReasonsMutations } from '../../../api';

interface Props {
  reason: CloseReason;
}

export const CloseReasonField = ({ reason }: Props) => {
  const { actions } = useBPFCloseReasonsMutations();

  const [reasonLabel, setReasonLabel] = useState(reason.label);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [updateStatusLoading, setUpdateStatusLoading] = useState(false);

  const lastSavedValueRef = useRef(reason.label);

  const saveLabel = useCallback(
    async (newLabel: string) => {
      if (newLabel.trim() === lastSavedValueRef.current.trim()) return;

      try {
        await actions.handleUpdateCloseReasonLabel(reason.id, newLabel.trim());
        lastSavedValueRef.current = newLabel.trim();
      } catch (error) {
        setReasonLabel(lastSavedValueRef.current);
      }
    },
    [actions, reason.id]
  );

  const saveLabelRef = useRef(saveLabel);

  useEffect(() => {
    saveLabelRef.current = saveLabel;
  }, [saveLabel]);

  const debouncedSearchChange = useRef(
    debounce((value: string) => {
      saveLabelRef.current(value);
    }, 500)
  );

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '2.5rem 1fr 8rem', gap: '1rem' }}>
      <Tooltip title="Remove reason">
        <IconButton
          size="small"
          disabled={deleteLoading}
          onClick={async () => {
            setDeleteLoading(true);
            await actions.handleDeleteCloseReason(reason.id);
            setDeleteLoading(false);
          }}
        >
          <Delete color="error" />
        </IconButton>
      </Tooltip>

      <FormInput
        variant="outlined"
        value={reasonLabel}
        placeholder="Enter reason"
        onChange={value => {
          setReasonLabel(value);
          debouncedSearchChange.current(value);
        }}
        onBlur={() => debouncedSearchChange.current.flush()}
      />

      <Box>
        <FormControlLabel
          control={<Switch />}
          checked={reason.isActive}
          disabled={updateStatusLoading}
          label={reason.isActive ? 'Active' : 'Inactive'}
          onChange={async (_, checked) => {
            setUpdateStatusLoading(true);
            await actions.handleUpdateCloseReasonStatus(reason.id, checked);
            setUpdateStatusLoading(false);
          }}
        />
      </Box>
    </Box>
  );
};
